import { createFileRoute } from "@tanstack/react-router"
import { createServerFn } from "@tanstack/react-start"
import { buildReportEmailHtml } from "~/lib/email/build-report-html"
import { getSanityClient } from "~/lib/sanity"
import { ALL_PROFILES_QUERY } from "~/lib/sanity-queries"
import { mapSanityProfile } from "~/lib/sanity-mappers"
import type { Profile } from "~/data/profiles"
import { profiles as fallbackProfiles } from "~/data/profiles"
import { captureHandledException } from "~/lib/handled-errors"

const getEmailPreviewHtml = createServerFn({ method: "GET" })
  .inputValidator((d: { slug?: string; key?: string }) => d)
  .handler(async ({ data }) => {
    const previewKey = process.env.DEV_PREVIEW_KEY
    if (previewKey && data.key !== previewKey) {
      return { html: "", slugs: [] as string[], error: "Unauthorized" }
    }
    let profiles: Profile[] = []
    try {
      const client = getSanityClient()
      const docs = await client.fetch(ALL_PROFILES_QUERY)
      if (Array.isArray(docs) && docs.length > 0) {
        profiles = (docs as Record<string, unknown>[]).map(mapSanityProfile)
      }
    } catch (err) {
      console.error("Sanity fetch failed, using fallback profiles:", err)
      await captureHandledException(err, {
        feature: "dev-email-preview",
        action: "load-profiles",
        layer: "server",
        fallback: "fallback-profiles",
      })
    }

    if (profiles.length === 0) {
      profiles = fallbackProfiles
    }

    const slug = data.slug || profiles[0]?.id
    const profile = profiles.find((p) => p.id === slug) || profiles[0]

    if (!profile) {
      return { html: "<p>No profiles found</p>", slugs: [] as string[], error: "No profiles available" }
    }

    try {
      const html = await buildReportEmailHtml({
        profile,
        firstName: "Preview",
        reportUrl: "http://localhost:3000/report/test-id",
        isDualProfile: false,
      })

      return { html, slugs: profiles.map((p) => p.id), error: null }
    } catch (err) {
      console.error("Email HTML build failed:", err)
      await captureHandledException(err, {
        feature: "dev-email-preview",
        action: "build-html",
        layer: "server",
        fallback: "inline-error-html",
        extra: { slug: profile.id },
      })
      const message = err instanceof Error ? err.message + "\n" + (err as Error).stack : String(err)
      return { html: `<pre style="color:red;white-space:pre-wrap">${message}</pre>`, slugs: profiles.map((p) => p.id), error: message }
    }
  })

export const Route = createFileRoute("/dev/email-preview")({
  component: EmailPreview,
  validateSearch: (search: Record<string, unknown>) => ({
    slug: (search.slug as string) || undefined,
    key: (search.key as string) || undefined,
  }),
  loaderDeps: ({ search }) => ({ slug: search.slug, key: search.key }),
  loader: async ({ deps }) => {
    return getEmailPreviewHtml({ data: { slug: deps.slug, key: deps.key } })
  },
})

function EmailPreview() {
  const { html, slugs, error } = Route.useLoaderData()
  const { slug, key } = Route.useSearch()

  if (error === "Unauthorized") {
    return (
      <div style={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh", background: "#222", color: "#999", fontFamily: "sans-serif" }}>
        <p>Not found</p>
      </div>
    )
  }

  return (
    <div style={{ background: "#222", minHeight: "100vh", padding: 20 }}>
      <div style={{ maxWidth: 860, margin: "0 auto 16px", display: "flex", gap: 8, flexWrap: "wrap" }}>
        {slugs.map((s) => (
          <a
            key={s}
            href={`/dev/email-preview?slug=${s}${key ? `&key=${key}` : ""}`}
            style={{
              padding: "6px 14px",
              borderRadius: 6,
              background: s === (slug || slugs[0]) ? "#F36523" : "#444",
              color: "#fff",
              textDecoration: "none",
              fontSize: 13,
              fontFamily: "sans-serif",
            }}
          >
            {s}
          </a>
        ))}
      </div>
      <div
        style={{ maxWidth: 860, margin: "0 auto" }}
        dangerouslySetInnerHTML={{ __html: html }}
      />
    </div>
  )
}
